import React, { useState } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  InputLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
} from "@mui/material";
import BreathingRateChart, { BreathingRateDataPoint } from "./index";
import theme from "@/theme";
import { convertPxToRem } from "@/utils";

const styleInputDefault = {
  fontSize: convertPxToRem(16),
  fontWeight: 600,
  color: theme.palette.neutral?.[900] || theme.palette.grey[900],
  padding: `${convertPxToRem(10)} ${convertPxToRem(14)}`,
};

const initialData: BreathingRateDataPoint[] = [
  {
    date: "20/01",
    breathingRate: 15,
    fullDate: "20.01.2025",
  },
  {
    date: "02/04",
    breathingRate: 11,
    fullDate: "02.04.2025",
  },
  {
    date: "04/04",
    breathingRate: 19,
    fullDate: "04.04.2025",
    isSelected: true,
  },
];

// Theme colors for scoring table
const themeColors = {
  error: theme.palette.error.main || "#d32f2f", // red
  warning: theme.palette.secondary[900] || "#f57c00", // amber
  success: theme.palette.accent1[700] || "#1b5e20", // green
};

export default function BreathingRateChartExamples() {
  const [data, setData] = useState<BreathingRateDataPoint[]>(initialData);
  const [newBreathingRate, setNewBreathingRate] = useState<string>("");
  const [entriesAdded, setEntriesAdded] = useState<number>(0);
  const MAX_NEW_ENTRIES = 2;

  const handleBreathingRateInput = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (value === "" || /^\d+$/.test(value)) {
      setNewBreathingRate(value);
    }
  };

  const handleAddDataPoint = () => {
    if (entriesAdded >= MAX_NEW_ENTRIES) {
      return;
    }

    const breathingRate = parseInt(newBreathingRate);

    if (!isNaN(breathingRate) && breathingRate > 0) {
      const now = new Date();
      const newPoint: BreathingRateDataPoint = {
        date: `${String(now.getDate()).padStart(2, "0")}/${String(now.getMonth() + 1).padStart(2, "0")}`,
        breathingRate,
        fullDate: `${String(now.getDate()).padStart(2, "0")}.${String(now.getMonth() + 1).padStart(2, "0")}.${now.getFullYear()}`,
        isSelected: true,
      };

      // Remove isSelected from all previous data points
      const updatedData = data.map((point) => ({
        ...point,
        isSelected: false,
      }));

      setData([...updatedData, newPoint]);
      setEntriesAdded(entriesAdded + 1);
      setNewBreathingRate("");
    }
  };

  return (
    <>
      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <BreathingRateChart data={data} />
      </Box>

      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 1,
            color: theme.palette.text.primary,
          }}
        >
          Add New Breathing Rate Reading
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            marginBottom: 2,
            color: theme.palette.grey[600],
          }}
        >
          You can add {MAX_NEW_ENTRIES - entriesAdded} more reading
          {MAX_NEW_ENTRIES - entriesAdded !== 1 ? "s" : ""}
        </Typography>

        <Stack direction="row" gap={1} alignItems="flex-end" flexWrap="wrap">
          <Stack spacing={1} sx={{ flex: 1 }}>
            <InputLabel htmlFor="breathing-rate-input">
              <Typography color="neutral.700" variant="body2" fontWeight={300}>
                Breathing Rate (breaths/min)
              </Typography>
            </InputLabel>
            <TextField
              id="breathing-rate-input"
              value={newBreathingRate}
              size="small"
              onChange={handleBreathingRateInput}
              placeholder="15"
              disabled={entriesAdded >= MAX_NEW_ENTRIES}
              slotProps={{
                htmlInput: {
                  autoComplete: "off",
                  "aria-label": "Breathing Rate",
                  inputMode: "numeric",
                },
              }}
              sx={{
                input: {
                  ...styleInputDefault,
                  fontSize: convertPxToRem(16),
                },
              }}
              fullWidth
            />
          </Stack>

          <Button
            variant="contained"
            onClick={handleAddDataPoint}
            disabled={!newBreathingRate || entriesAdded >= MAX_NEW_ENTRIES}
            sx={{
              paddingTop: convertPxToRem(10),
              paddingBottom: convertPxToRem(10),
              paddingLeft: convertPxToRem(20),
              paddingRight: convertPxToRem(20),
              minWidth: 100,
            }}
          >
            Add
          </Button>
        </Stack>

        {entriesAdded >= MAX_NEW_ENTRIES && (
          <Typography
            sx={{
              marginTop: 2,
              fontSize: 12,
              color: theme.palette.error.main,
              fontWeight: 600,
            }}
          >
            Maximum of {MAX_NEW_ENTRIES} new entries reached
          </Typography>
        )}
      </Box>

      <Box sx={{ padding: 2, marginBottom: 1 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: 14,
            fontWeight: 600,
            marginBottom: 2,
            color: theme.palette.text.primary,
          }}
        >
          Scoring Rules
        </Typography>

        <Stack spacing={3}>
          <Box>
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 600,
                marginBottom: 1,
                color: theme.palette.text.primary,
              }}
            >
              Breathing Rate
            </Typography>
            <TableContainer
              component={Paper}
              sx={{
                boxShadow: "none",
                border: `1px solid ${theme.palette.grey[300]}`,
              }}
            >
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                      Range (breaths/min)
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: 12 }}>
                      Category
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12 }}>&lt;12/min</TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: themeColors.warning,
                        fontWeight: 600,
                      }}
                    >
                      AMBER
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12 }}>12 - 18/min</TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: themeColors.success,
                        fontWeight: 600,
                      }}
                    >
                      GREEN
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12 }}>19 - 20/min</TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: themeColors.warning,
                        fontWeight: 600,
                      }}
                    >
                      AMBER
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontSize: 12 }}>&gt;20/min</TableCell>
                    <TableCell
                      sx={{
                        fontSize: 12,
                        color: themeColors.error,
                        fontWeight: 600,
                      }}
                    >
                      RED
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Stack>
      </Box>
    </>
  );
}
